// Door-Dash-only decor: pink/purple straight track with rows of doors at
// fixed s-positions. Each row has 4 lane-cells; a seeded random function
// decides which cells are "open" (green frame, passable) vs "blocked"
// (red X, solid wall). Doors get re-rolled each run via the same seed.
//
// Layout positions come from Path.doorDash (computed in Path.buildDoorDash).
// Spinners and checkpoint gates are placed by the Spawner using the same
// arrays — this module just owns visual + collision of the doors AND the
// solid ground platform under everything.

import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";
import { createRng } from "@shared/game";

const TRACK_TOP_Y = 0.18;
const TRACK_COLOR = 0x9b5cf6; // purple
const TRACK_STRIPE = 0xff5fa7; // pink
const FRAME_OPEN = 0x3ed47e;
const FRAME_BLOCKED = 0xff3b3b;
const DOOR_HEIGHT = 2.4;

export interface DoorCell {
  /** Door center on s. */
  s: number;
  /** Door center lateral offset. */
  l: number;
  /** Half-extents for collision footprint. */
  halfDepth: number;
  halfWidth: number;
  /** Authoritative state. Open doors don't block on contact; blocked doors do. */
  open: boolean;
  /** True once a player has run into this door and revealed its state.
   *  Open doors shatter; blocked doors stay put (no red flash). */
  revealed: boolean;
  /** ms when the door was first hit, drives the open/close animation. */
  revealedAt: number | null;
  /** Used for visual animation (slight bob). */
  mesh: THREE.Group;
  slab: THREE.Mesh;
  /** Spawned debris when an open door is shattered. Tracked here so the tick
   *  loop can advance their physics + fade. */
  debris: DebrisPiece[];
}

interface DebrisPiece {
  mesh: THREE.Mesh;
  vx: number; vy: number; vz: number;
  rx: number; ry: number; rz: number;
}

export interface DoorDashHandle {
  group: THREE.Group;
  /** Meshes the player can stand on (the long ground platform). */
  supportMeshes: THREE.Object3D[];
  /** All doors (open + blocked). Game queries `solid` flag for collision. */
  doors: DoorCell[];
  /** Spinner positions, exposed for the Spawner. */
  spinnerPositions: number[];
  /** Gate positions, exposed for the Spawner. */
  gatePositions: number[];
  /** Game calls this on first contact; the slab animates open (door.open=true)
   *  or flashes red (door.open=false) so the player learns the result. */
  reveal: (door: DoorCell, now: number) => void;
  tick: (clockMs: number, performanceNow: number) => void;
}

interface DoorMeshes {
  group: THREE.Group;
  /** The slab that fills the doorway. Hidden when the door is revealed-open. */
  slab: THREE.Mesh;
}

function buildDoor(width: number): DoorMeshes {
  const g = new THREE.Group();
  // Neutral frame + slab — players can't tell open from blocked until they
  // collide. The slab stays for blocked doors; on open doors it swings out of
  // sight on contact (handled by Game).
  const frameMat = new THREE.MeshStandardMaterial({
    color: 0xffd166,
    emissive: 0xc88a14,
    emissiveIntensity: 0.14,
    roughness: 0.5,
  });
  for (const x of [-width / 2 + 0.16, width / 2 - 0.16]) {
    const post = new THREE.Mesh(new SoftBox(0.28, DOOR_HEIGHT, 0.28), frameMat);
    post.position.set(x, DOOR_HEIGHT / 2 + TRACK_TOP_Y, 0);
    post.castShadow = true;
    g.add(post);
  }
  const header = new THREE.Mesh(new SoftBox(width, 0.34, 0.34), frameMat);
  header.position.set(0, DOOR_HEIGHT + 0.17 + TRACK_TOP_Y, 0);
  header.castShadow = true;
  g.add(header);

  // Slab: identical purple "?" door for every cell. Decorated with a soft
  // white question-mark plate so the player understands they don't know
  // what's behind it yet.
  const slab = new THREE.Mesh(
    new SoftBox(width - 0.35, DOOR_HEIGHT - 0.2, 0.2),
    new THREE.MeshStandardMaterial({
      color: 0x8b5cf6,
      emissive: 0x4b2da6,
      emissiveIntensity: 0.18,
      roughness: 0.5,
    }),
  );
  slab.position.set(0, DOOR_HEIGHT / 2 + TRACK_TOP_Y, 0);
  slab.castShadow = true;
  slab.receiveShadow = true;
  g.add(slab);
  // "?" plate on the slab.
  const plate = new THREE.Mesh(
    new THREE.PlaneGeometry(width * 0.55, (DOOR_HEIGHT - 0.2) * 0.55),
    new THREE.MeshBasicMaterial({
      map: questionMarkTexture(),
      transparent: true,
      depthWrite: false,
    }),
  );
  plate.position.set(0, DOOR_HEIGHT / 2 + TRACK_TOP_Y, 0.12);
  g.add(plate);

  return { group: g, slab };
}

let _qmTex: THREE.CanvasTexture | null = null;
function questionMarkTexture(): THREE.CanvasTexture {
  if (_qmTex) return _qmTex;
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, 256, 256);
  ctx.fillStyle = "#ffffff";
  ctx.font = "900 220px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("?", 128, 138);
  _qmTex = new THREE.CanvasTexture(canvas);
  _qmTex.colorSpace = THREE.SRGBColorSpace;
  return _qmTex;
}

/** Build a single ground ribbon that tapers between the given (s, halfWidth)
 *  control points. The TOP face is its own mesh (always-lit, brighter) so
 *  the player can clearly see the road; the sides are a darker sub-mesh
 *  for visual depth. Previously we baked top + sides into one buffer with
 *  a shared material, which made the lit top look identical to the back
 *  side and the floor disappeared into the background. */
function buildTaperedGround(points: { s: number; halfW: number }[]): THREE.Group {
  const g = new THREE.Group();
  // Door-dash ground is now an extruded slab (≥ 0.5m thick) instead of
  // the previous flat ribbon. Builds: top face + bottom face + left/
  // right side walls so it reads as a real platform from any angle.
  const TOP_Y = 0.18;
  const THICKNESS = 0.55;
  const BOT_Y = TOP_Y - THICKNESS;
  const topPositions: number[] = [];
  const topIndices: number[] = [];
  // Ring layout per row: 0=top-left, 1=top-right, 2=bot-left, 3=bot-right.
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    topPositions.push(-p.halfW, TOP_Y, p.s);
    topPositions.push(+p.halfW, TOP_Y, p.s);
    topPositions.push(-p.halfW, BOT_Y, p.s);
    topPositions.push(+p.halfW, BOT_Y, p.s);
  }
  for (let i = 0; i < points.length - 1; i++) {
    const a = i * 4;
    // Top face (CCW from above so normals point up).
    topIndices.push(a + 0, a + 4, a + 1);
    topIndices.push(a + 1, a + 4, a + 5);
    // Bottom face (CCW from below so normals point down).
    topIndices.push(a + 2, a + 3, a + 6);
    topIndices.push(a + 3, a + 7, a + 6);
    // Left side wall.
    topIndices.push(a + 0, a + 2, a + 4);
    topIndices.push(a + 4, a + 2, a + 6);
    // Right side wall.
    topIndices.push(a + 1, a + 5, a + 3);
    topIndices.push(a + 3, a + 5, a + 7);
  }
  const topGeo = new THREE.BufferGeometry();
  topGeo.setAttribute("position", new THREE.Float32BufferAttribute(topPositions, 3));
  topGeo.setIndex(topIndices);
  topGeo.computeVertexNormals();
  const topMat = new THREE.MeshStandardMaterial({
    color: 0xc4a3ff,
    emissive: 0x4b2da6,
    emissiveIntensity: 0.18,
    roughness: 0.5,
  });
  const top = new THREE.Mesh(topGeo, topMat);
  top.receiveShadow = true;
  top.castShadow = true;
  g.add(top);

  // ── Edge stripes on each side of the top face for extra visibility. ──
  const stripePositions: number[] = [];
  const stripeIndices: number[] = [];
  const STRIPE_W = 0.35;
  for (const side of [-1, 1] as const) {
    const startIdx = stripePositions.length / 3;
    for (let i = 0; i < points.length; i++) {
      const p = points[i]!;
      const outer = side * p.halfW;
      const inner = side * (p.halfW - STRIPE_W);
      stripePositions.push(Math.min(outer, inner), 0.20, p.s);
      stripePositions.push(Math.max(outer, inner), 0.20, p.s);
    }
    for (let i = 0; i < points.length - 1; i++) {
      const a = startIdx + i * 2;
      stripeIndices.push(a, a + 2, a + 1);
      stripeIndices.push(a + 1, a + 2, a + 3);
    }
  }
  const stripeGeo = new THREE.BufferGeometry();
  stripeGeo.setAttribute("position", new THREE.Float32BufferAttribute(stripePositions, 3));
  stripeGeo.setIndex(stripeIndices);
  stripeGeo.computeVertexNormals();
  const stripeMat = new THREE.MeshStandardMaterial({
    color: 0xffd166,
    emissive: 0xff8a3d,
    emissiveIntensity: 0.25,
    roughness: 0.4,
    side: THREE.DoubleSide,
  });
  g.add(new THREE.Mesh(stripeGeo, stripeMat));

  // ── Side walls so the ground looks like a slab, not a sticker. ──
  const sidePositions: number[] = [];
  const sideIndices: number[] = [];
  for (const side of [-1, 1] as const) {
    const startIdx = sidePositions.length / 3;
    for (let i = 0; i < points.length; i++) {
      const p = points[i]!;
      const x = side * p.halfW;
      sidePositions.push(x, 0.18, p.s);
      sidePositions.push(x, -0.20, p.s);
    }
    for (let i = 0; i < points.length - 1; i++) {
      const a = startIdx + i * 2;
      if (side === -1) {
        sideIndices.push(a, a + 1, a + 2);
        sideIndices.push(a + 1, a + 3, a + 2);
      } else {
        sideIndices.push(a, a + 2, a + 1);
        sideIndices.push(a + 1, a + 2, a + 3);
      }
    }
  }
  const sideGeo = new THREE.BufferGeometry();
  sideGeo.setAttribute("position", new THREE.Float32BufferAttribute(sidePositions, 3));
  sideGeo.setIndex(sideIndices);
  sideGeo.computeVertexNormals();
  const sideMat = new THREE.MeshStandardMaterial({ color: TRACK_COLOR, roughness: 0.6 });
  g.add(new THREE.Mesh(sideGeo, sideMat));

  return g;
}

function buildGroundStripe(length: number, halfWidth: number): THREE.Group {
  const g = new THREE.Group();
  const trackWidth = halfWidth * 2;
  // Base purple track
  const base = new THREE.Mesh(
    new SoftBox(trackWidth, 0.4, length),
    new THREE.MeshStandardMaterial({ color: TRACK_COLOR, roughness: 0.55 }),
  );
  base.position.set(0, 0, length / 2);
  base.receiveShadow = true;
  base.castShadow = true;
  g.add(base);
  // Pink edge stripes
  for (const sign of [-1, 1] as const) {
    const stripe = new THREE.Mesh(
      new SoftBox(0.55, 0.18, length),
      new THREE.MeshStandardMaterial({ color: TRACK_STRIPE, roughness: 0.42 }),
    );
    stripe.position.set(sign * (halfWidth - 0.3), 0.21, length / 2);
    g.add(stripe);
  }
  return g;
}

export function buildDoorDashCourse(path: Path, seed: number): DoorDashHandle {
  const dd = path.doorDash;
  if (!dd) throw new Error("buildDoorDashCourse called on non-door-dash path");

  const group = new THREE.Group();
  const supportMeshes: THREE.Object3D[] = [];
  const doors: DoorCell[] = [];

  // The road TAPERS along its length: full width at the start, narrower
  // at each successive door row (each cell stays the same width, the road
  // around the cells shrinks). We pre-compute the per-row half-widths
  // first so the ground tapering can match the door geometry exactly.
  const rowCountForGround = dd.doorRows.length;
  const CELL_WIDTH = 1.7; // ← every door is THIS wide, top of course to bottom
  const rowHalfWidths: number[] = [];
  for (let r = 0; r < rowCountForGround; r++) {
    const cells = Math.max(1, rowCountForGround - r);
    rowHalfWidths.push((cells * CELL_WIDTH) / 2);
  }
  // Build the tapering ground from start → row 0 → row 1 → … → finish.
  const groundPoints: { s: number; halfW: number }[] = [];
  groundPoints.push({ s: 0, halfW: dd.halfWidth });
  groundPoints.push({ s: Math.max(0, dd.doorRows[0]! - 4), halfW: dd.halfWidth });
  for (let r = 0; r < rowCountForGround; r++) {
    groundPoints.push({ s: dd.doorRows[r]!, halfW: rowHalfWidths[r]! });
  }
  groundPoints.push({ s: path.length - 4, halfW: rowHalfWidths[rowCountForGround - 1]! });
  groundPoints.push({ s: path.length, halfW: rowHalfWidths[rowCountForGround - 1]! });
  const ground = buildTaperedGround(groundPoints);
  group.add(ground);
  // The TOP face is the first child of the ground group — that's what the
  // standable-surface raycaster needs to catch. Stripes and side walls
  // hang off the top and don't act as standable surfaces.
  const groundTop = ground.children.find((c) => c instanceof THREE.Mesh) as THREE.Mesh | undefined;
  if (groundTop) supportMeshes.push(groundTop);

  // Door rows: every door is a CONSTANT width (CELL_WIDTH = 1.7m). The road
  // narrows row by row because fewer cells fit. Side walls flank each row,
  // filling the gap between the row's edge and the still-wider track at
  // that s position, so the player can't bypass through the gap.
  const rng = createRng(seed ^ 0x444043);
  const rowCount = dd.doorRows.length; // 8
  const wallMat = new THREE.MeshStandardMaterial({
    color: 0x6c43c5,
    emissive: 0x2f1a72,
    emissiveIntensity: 0.18,
    roughness: 0.55,
  });
  for (let r = 0; r < rowCount; r++) {
    const sCenter = dd.doorRows[r]!;
    // Cells: 8, 7, 6, 5, 4, 3, 2, 1.
    const cells = Math.max(1, rowCount - r);
    const cellWidth = CELL_WIDTH;
    const rowHalfWidth = (cells * cellWidth) / 2;

    // No side walls anymore — the road itself tapers to exactly cellWidth ×
    // cells at each row, so the row of doors completely fills the road's
    // cross-section. There's nowhere to bypass.
    void wallMat;

    // Half open, half blocked — alternating so the answer always looks
    // ambiguous from a distance. Guarantee at least one open cell per row.
    const states: boolean[] = [];
    const openTargetExact = Math.max(1, Math.floor(cells / 2));
    for (let c = 0; c < cells; c++) {
      states.push(c < openTargetExact);
    }
    // Shuffle (Fisher-Yates with the seeded rng) so the open positions
    // aren't always the left-most.
    for (let i = cells - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [states[i], states[j]] = [states[j]!, states[i]!];
    }

    for (let c = 0; c < cells; c++) {
      const open = states[c]!;
      const lCenter = -rowHalfWidth + cellWidth * (c + 0.5);
      const { group: doorMesh, slab } = buildDoor(cellWidth - 0.1);
      doorMesh.position.set(lCenter, 0, sCenter);
      group.add(doorMesh);
      doors.push({
        s: sCenter,
        l: lCenter,
        halfDepth: 0.32,
        halfWidth: (cellWidth - 0.1) / 2,
        open,
        revealed: false,
        revealedAt: null,
        mesh: doorMesh,
        slab,
        debris: [],
      });
    }
  }

  // Small breathing-room platforms between sections — visual: pink podium
  // under each spinner + gate so they read as "stations". (Decorative only;
  // the ground already supports the player here.)
  for (const z of dd.spinners) {
    const pad = new THREE.Mesh(
      new SoftBox(dd.halfWidth * 1.4, 0.18, 4.6),
      new THREE.MeshStandardMaterial({ color: TRACK_STRIPE, roughness: 0.45 }),
    );
    pad.position.set(0, 0.22, z);
    group.add(pad);
  }
  for (const z of dd.gates) {
    const pad = new THREE.Mesh(
      new SoftBox(dd.halfWidth * 1.2, 0.18, 3.4),
      new THREE.MeshStandardMaterial({ color: 0xffd166, roughness: 0.45 }),
    );
    pad.position.set(0, 0.22, z);
    group.add(pad);
  }

  const REVEAL_MS = 700;
  /** Spawn N chunky cube shards from the slab's centre with random
   *  outward velocity + spin. They fall under gravity and fade out. */
  function spawnDebris(door: DoorCell): DebrisPiece[] {
    const pieces: DebrisPiece[] = [];
    const PIECES = 8;
    const slabSize = door.halfWidth * 2;
    const chunkSize = slabSize / 3.2;
    for (let i = 0; i < PIECES; i++) {
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(chunkSize, chunkSize, chunkSize * 0.6),
        new THREE.MeshStandardMaterial({
          color: 0x8b5cf6,
          emissive: 0x4b2da6,
          emissiveIntensity: 0.25,
          roughness: 0.55,
          transparent: true,
          opacity: 1,
        }),
      );
      mesh.position.set(
        door.l + (Math.random() - 0.5) * door.halfWidth,
        DOOR_HEIGHT / 2 + TRACK_TOP_Y + (Math.random() - 0.5) * 0.8,
        door.s,
      );
      mesh.castShadow = true;
      group.add(mesh);
      pieces.push({
        mesh,
        vx: (Math.random() - 0.5) * 4,
        vy: 1.5 + Math.random() * 3.5,
        vz: -2 - Math.random() * 3,
        rx: (Math.random() - 0.5) * 6,
        ry: (Math.random() - 0.5) * 6,
        rz: (Math.random() - 0.5) * 6,
      });
    }
    return pieces;
  }

  const reveal = (door: DoorCell, now: number) => {
    if (door.revealed) return;
    door.revealed = true;
    door.revealedAt = now;
    if (door.open) {
      // Hide the slab immediately + spawn the explosion shards.
      door.slab.visible = false;
      door.debris = spawnDebris(door);
    }
    // Blocked doors: no reveal animation. The slab just stays put — there's
    // no red flash; the player learns by being stopped by it.
  };

  // Door bob + tick the shards on every open-door explosion.
  const tick = (clockMs: number, performanceNow: number) => {
    const t = clockMs / 1000;
    for (let i = 0; i < doors.length; i++) {
      const d = doors[i]!;
      const phase = i * 0.7;
      d.mesh.position.y = Math.sin(t * 1.6 + phase) * 0.05;
      if (d.debris.length > 0 && d.revealedAt != null) {
        const age = performanceNow - d.revealedAt;
        const dt = 1 / 60;
        const fadeT = Math.min(1, age / REVEAL_MS);
        for (const p of d.debris) {
          p.vy -= 14 * dt;
          p.mesh.position.x += p.vx * dt;
          p.mesh.position.y += p.vy * dt;
          p.mesh.position.z += p.vz * dt;
          p.mesh.rotation.x += p.rx * dt;
          p.mesh.rotation.y += p.ry * dt;
          p.mesh.rotation.z += p.rz * dt;
          const mat = p.mesh.material as THREE.MeshStandardMaterial;
          mat.opacity = Math.max(0, 1 - fadeT);
        }
        if (age >= REVEAL_MS) {
          // Done — clean up the shards so we don't keep ticking them.
          for (const p of d.debris) group.remove(p.mesh);
          d.debris = [];
        }
      }
    }
  };

  return {
    group,
    supportMeshes,
    doors,
    spinnerPositions: dd.spinners,
    gatePositions: dd.gates,
    reveal,
    tick,
  };
}
